import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { CubixLockup } from '@/components/shared/CubixMark';
import { login as loginApi } from '@/lib/auth/api';
import { useAuthStore } from '@/lib/auth/store';
import { useIsAuthenticated } from '@/lib/auth/hooks';

interface LocationState {
  from?: { pathname?: string };
}

export function LoginPage() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const setToken  = useAuthStore((s) => s.setToken);
  const isAuth    = useIsAuthenticated();

  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [errMsg,   setErrMsg]   = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: loginApi,
    onSuccess: (res) => {
      setToken(res.token);
      const from = (location.state as LocationState | null)?.from?.pathname ?? '/';
      navigate(from, { replace: true });
    },
    onError: (err: Error) => {
      setErrMsg(err.message ?? 'Login failed');
    },
  });

  if (isAuth) return <Navigate to="/" replace />;

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrMsg(null);
    mutation.mutate({ email, password });
  }

  return (
    <div className="flex min-h-screen bg-[#085041]">
      {/* Left panel — brand */}
      <div className="hidden lg:flex lg:w-[420px] lg:flex-col lg:justify-between lg:px-12 lg:py-10">
        <CubixLockup markSize={36} dark={false} />
        <div>
          <p
            className="text-[15px] leading-relaxed text-white/60"
            style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontStyle: 'italic' }}
          >
            "The financial operating system for mid-to-large banks in Africa and the Middle East."
          </p>
          <p className="mt-3 text-[11px] tracking-wider text-white/30 uppercase">
            ManTrust · ALM Platform
          </p>
        </div>
        <p className="text-[10px] text-white/20">
          cx-web · v1.20.0 · {new Date().getFullYear()}
        </p>
      </div>

      {/* Right panel — login form */}
      <div className="flex flex-1 items-center justify-center bg-slate-50 px-6">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="mb-8 flex justify-center lg:hidden">
            <CubixLockup markSize={32} dark={true} />
          </div>

          <div className="mb-6">
            <h1 className="text-xl font-semibold text-foreground">Sign in</h1>
            <p className="mt-1 text-[13px] text-muted-foreground">
              Multi-tenant ALM platform · ManTrust
            </p>
          </div>

          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-0 pt-0" />
            <CardContent className="pt-4">
              <form className="space-y-4" onSubmit={handleSubmit}>
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-[12px]">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="username"
                    placeholder="admin@cubix.local"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-9 text-[13px]"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password" className="text-[12px]">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-9 text-[13px]"
                  />
                </div>

                {errMsg && (
                  <p className="rounded-md bg-cx-danger-light px-3 py-2 text-[12px] text-cx-danger" role="alert">
                    {errMsg}
                  </p>
                )}

                <Button
                  type="submit"
                  className="w-full bg-cx-brand text-white hover:bg-cx-brand-dark"
                  disabled={mutation.isPending}
                >
                  {mutation.isPending ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Signing in…</>
                  ) : (
                    'Sign in'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
